import { ReactComponent as IconInfo } from "@common_assets/svg/info-circle.svg"
import { ReactComponent as Trash } from "@common_assets/svg/trash.svg"
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Fab,
  Stack,
  Tooltip,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import { MATCHING_IDS_LIMIT } from "utils/reactQuery"
import { SelectionBanner } from "utils/transactionSelection"

interface IProps {
  selectedCount: number
  banner: SelectionBanner | null
  onSelectAllMatching: () => void
  onClearSelection: () => void
  onRetry: () => void
  onDeleteSelected: () => void
}

interface IContent {
  title: string
  // Without onClick the action is a disabled status ("Counting…", too many).
  action?: {
    label: string
    // Why the action is unavailable; shown in a tooltip.
    detail?: string
    onClick?: () => void
    isError?: boolean
  }
}

const formatCount = (count: number) => count.toLocaleString("en-US")

// One sentence on the left says what is selected; the select-all-matching
// status or action sits on the right.
const useContent = ({
  selectedCount,
  banner,
  onSelectAllMatching,
  onClearSelection,
  onRetry,
}: IProps): IContent => {
  const { t } = useTranslation()

  if (!banner) {
    return {
      title: t("allTransactions.transactionsSelected", { count: selectedCount }),
    }
  }

  const clear = {
    label: t("allTransactions.clearSelection"),
    onClick: onClearSelection,
  }

  switch (banner.kind) {
    case "allSelected":
      return {
        title: t("allTransactions.allMatchingSelected", {
          total: formatCount(banner.total),
        }),
        action: clear,
      }
    case "partial":
      return {
        title: t("allTransactions.someMatchingSelected", {
          selected: formatCount(banner.selected),
          total: formatCount(banner.total),
        }),
        action: clear,
      }
  }

  const title = t("allTransactions.allLoadedSelected", {
    count: banner.loaded,
    formattedCount: formatCount(banner.loaded),
  })

  switch (banner.kind) {
    case "loading":
      return { title, action: { label: t("allTransactions.countingMatching") } }
    case "tooMany":
      return {
        title,
        action: {
          label: t("allTransactions.cantSelectAll"),
          detail:
            banner.detail ||
            t("allTransactions.tooManyMatching", {
              limit: formatCount(MATCHING_IDS_LIMIT),
            }),
          isError: true,
        },
      }
    case "error":
      return {
        title,
        action: {
          label: t("allTransactions.couldntCountRetry"),
          onClick: onRetry,
          isError: true,
        },
      }
    case "offer":
      return {
        title,
        action: {
          label: t("allTransactions.selectAllMatching", {
            total: formatCount(banner.total),
          }),
          onClick: onSelectAllMatching,
        },
      }
  }
}

const TransactionsSelectionBar = (props: IProps) => {
  const { t } = useTranslation()
  const content = useContent(props)

  if (!props.selectedCount) return null

  return (
    <Box p={2}>
      <Alert
        role="status"
        severity="info"
        sx={{
          alignItems: "center",
          bgcolor: "mockup.primary95",
          color: "mockup.neutral10",
          border: 1,
          borderColor: "mockup.primary90",
          "& .MuiAlert-icon": { color: "mockup.primary60", py: 0 },
          "& .MuiAlert-message": { display: "flex", alignItems: "center" },
          "& .MuiAlert-action": { alignItems: "center", pt: 0 },
          "& .MuiAlertTitle-root": { color: "mockup.darkBlue", mb: 0 },
        }}
        action={
          <Stack direction="row" gap={1} alignItems="center">
            {content.action && (
              <Tooltip
                title={content.action.detail ?? ""}
                arrow={true}
                describeChild
              >
                {/* A disabled button gets no hover or focus, so the wrapper
                    carries the tooltip. */}
                <span tabIndex={content.action.detail ? 0 : undefined}>
                  <Button
                    size="small"
                    variant="text"
                    disabled={!content.action.onClick}
                    onClick={content.action.onClick}
                    endIcon={content.action.detail ? <IconInfo /> : undefined}
                    sx={[
                      // Link-like: no outline after a mouse click (the theme
                      // styles :focus), keyboard focus stays visible.
                      (theme) => ({
                        color: "primary.main",
                        "&:hover": {
                          color: "primary.main",
                          textDecoration: "underline",
                        },
                        "&:focus": { outline: "none", color: "primary.main" },
                        "&.Mui-focusVisible": {
                          outline: `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: 2,
                        },
                      }),
                      content.action.isError
                        ? {
                            color: "mockup.error30",
                            "&:focus, &:hover": { color: "mockup.error30" },
                            "&.Mui-disabled": { color: "mockup.error30" },
                          }
                        : { "&.Mui-disabled": { color: "mockup.neutral30" } },
                    ]}
                  >
                    {content.action.label}
                    {content.action.detail && (
                      <Box component="span" sx={visuallyHiddenSx}>
                        {` ${content.action.detail}`}
                      </Box>
                    )}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Fab
              size="small"
              aria-label={t("allTransactions.removeSelected")}
              onClick={props.onDeleteSelected}
            >
              <Trash />
            </Fab>
          </Stack>
        }
      >
        <AlertTitle>{content.title}</AlertTitle>
      </Alert>
    </Box>
  )
}

export default TransactionsSelectionBar
